import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, IconButton, Typography, Stack, TextField, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BsChatDots, BsMicFill, BsVolumeUpFill, BsVolumeMuteFill } from "react-icons/bs";
import { RxCross2 } from "react-icons/rx";
import { IoSend } from "react-icons/io5";
import { service_booking_keys } from "../../store/constants";

const CHAT_BASE = "https://zuno-backend-production-7f96.up.railway.app/api/web-chat";
const CHAT_API = `${CHAT_BASE}/message`;
const SPEAK_API = `${CHAT_BASE}/speak`;
const ACCENT = "#F15A24";
const INK = "#0F2231";

const GREETING = "Hey! I'm the Done assistant. Tell me what's going on with your home and I can quote you a price and help you get booked.";

// Voice input uses the browser's built-in speech recognition (instant, free,
// no audio round-trip). Voice OUTPUT uses the same ElevenLabs voice as the
// phone assistant, via our backend, so the AI sounds the same everywhere —
// worth the extra ~1 request of latency for a real voice instead of the
// robotic browser TTS.
const SpeechRecognitionAPI = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const speechSupported = !!SpeechRecognitionAPI;

const ChatWidget = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, []);

  const speakText = useCallback(async (text) => {
    if (!text) return;
    if (audioRef.current) audioRef.current.pause();
    try {
      setSpeaking(true);
      const res = await fetch(SPEAK_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("tts failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch (e) {
      setSpeaking(false);
    }
  }, []);

  const send = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(CHAT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: nextMessages.map(({ role, content }) => ({ role, content })) }),
      });
      const data = await res.json();
      setMessages([...nextMessages, { role: "assistant", content: data.reply, booking: data.booking || null }]);
      if (voiceOn) speakText(data.reply);
    } catch (e) {
      const errText = "Sorry, something went wrong — mind trying again?";
      setMessages([...nextMessages, { role: "assistant", content: errText }]);
      if (voiceOn) speakText(errText);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, voiceOn, speakText]);

  const startListening = () => {
    if (!speechSupported || listening) return;
    const recognition = new SpeechRecognitionAPI();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (e) => {
      const transcript = e.results?.[0]?.[0]?.transcript || "";
      if (transcript) {
        setVoiceOn(true); // once they talk to it, talk back
        send(transcript);
      }
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
  };

  const handleBook = (booking) => {
    try {
      localStorage.setItem(service_booking_keys.DESCRIPTION, booking.summary || "");
    } catch (e) {}
    navigate(`/service-details/${booking.slug}`);
    setOpen(false);
  };

  return (
    <>
      <IconButton
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat with Done assistant"
        sx={{
          position: "fixed",
          bottom: { xs: 20, md: 28 },
          right: { xs: 20, md: 28 },
          width: 58,
          height: 58,
          backgroundColor: ACCENT,
          color: "#fff",
          boxShadow: "0 8px 24px -6px rgba(241,90,36,0.5)",
          zIndex: 1300,
          "&:hover": { backgroundColor: "#D94E1C" },
        }}
      >
        {open ? <RxCross2 size={24} /> : <BsChatDots size={24} />}
      </IconButton>

      {open && (
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 90, md: 100 },
            right: { xs: 16, md: 28 },
            left: { xs: 16, md: "auto" },
            width: { xs: "auto", md: 360 },
            height: { xs: "60vh", md: 480 },
            backgroundColor: "#fff",
            borderRadius: "18px",
            boxShadow: "0 20px 60px -12px rgba(15,34,49,0.35)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 1300,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ backgroundColor: INK, color: "#fff", px: 2.5, py: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>Done Assistant</Typography>
              <Typography sx={{ fontSize: "12px", opacity: 0.7 }}>
                {speaking ? "Speaking…" : speechSupported ? "Type or tap the mic to talk" : "Ask about pricing or get booked"}
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                if (voiceOn && audioRef.current) audioRef.current.pause();
                setVoiceOn((v) => !v);
              }}
              aria-label={voiceOn ? "Turn off spoken replies" : "Turn on spoken replies"}
              sx={{ color: voiceOn ? ACCENT : "rgba(255,255,255,0.6)" }}
            >
              {voiceOn ? <BsVolumeUpFill size={18} /> : <BsVolumeMuteFill size={18} />}
            </IconButton>
          </Stack>

          <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
            <Stack spacing={1.5}>
              {messages.map((m, i) => (
                <Box key={i}>
                  <Box
                    sx={{
                      alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                      ml: m.role === "user" ? "auto" : 0,
                      mr: m.role === "user" ? 0 : "auto",
                      maxWidth: "85%",
                      backgroundColor: m.role === "user" ? ACCENT : "#F4F0E9",
                      color: m.role === "user" ? "#fff" : INK,
                      px: 1.75,
                      py: 1,
                      borderRadius: "14px",
                      fontSize: "14px",
                      lineHeight: 1.5,
                    }}
                  >
                    {m.content}
                  </Box>
                  {m.booking && (
                    <Box
                      onClick={() => handleBook(m.booking)}
                      sx={{
                        mt: 1,
                        cursor: "pointer",
                        border: `1px solid ${ACCENT}40`,
                        backgroundColor: "#FFF1EA",
                        borderRadius: "12px",
                        px: 1.75,
                        py: 1.25,
                        maxWidth: "85%",
                        transition: "all 0.2s ease",
                        "&:hover": { backgroundColor: "#FFE4D4" },
                      }}
                    >
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK }}>
                        {m.booking.serviceName} — ${m.booking.price}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: ACCENT, fontWeight: 600, mt: 0.25 }}>
                        Continue to booking →
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
              {loading && (
                <Box sx={{ alignSelf: "flex-start" }}>
                  <CircularProgress size={16} sx={{ color: ACCENT }} />
                </Box>
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: "1px solid #EEF0F2" }}>
            <TextField
              fullWidth
              size="small"
              placeholder={listening ? "Listening…" : "Type a message…"}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "14px" } }}
            />
            {speechSupported && (
              <IconButton
                onClick={listening ? stopListening : startListening}
                aria-label={listening ? "Stop listening" : "Talk to the assistant"}
                sx={{
                  color: listening ? "#fff" : ACCENT,
                  backgroundColor: listening ? ACCENT : "transparent",
                  "&:hover": { backgroundColor: listening ? "#D94E1C" : "rgba(241,90,36,0.08)" },
                }}
              >
                <BsMicFill size={18} />
              </IconButton>
            )}
            <IconButton onClick={() => send()} disabled={loading || !input.trim()} sx={{ color: ACCENT }}>
              <IoSend size={20} />
            </IconButton>
          </Stack>
        </Box>
      )}
    </>
  );
};

export default ChatWidget;
